import type { EntityFormConfig } from '@components/integrated/entity-form';
import {
  ERP_ENVIRONMENT_NONE_OPTION,
  ERP_ENVIRONMENT_OPTIONS,
  ERP_ENVIRONMENT_PLACEHOLDER,
} from './erpEnvironment';
import {
  createFormActions,
  createFormEndpoint,
  createFormCreateId,
  createFormEditId,
  createFormLayout,
  createFormWorkflow,
} from './formConfigHelpers';

/**
 * Returns an EntityFormConfig for ERP Registry create or edit mode.
 *
 * @param mode - 'create' or 'edit'
 * @param registrySlug - Required for edit mode; the slug of the registry being edited
 */
export function getErpRegistryFormConfig(
  mode: 'create' | 'edit',
  registrySlug?: string,
): EntityFormConfig {
  const isEdit = mode === 'edit';
  const basePath = '/api/admin/erp/registries';
  const registryEndpoint = registrySlug ? `${basePath}/${registrySlug}` : basePath;

  return {
    id: isEdit ? createFormEditId('erp-registry', registrySlug ?? 'unknown') : createFormCreateId('erp-registry'),

    endpoint: createFormEndpoint(
      isEdit,
      basePath,
      isEdit ? registryEndpoint : basePath,
      !!registrySlug,
      [
        'slug', 'name', 'base_url', 'environment', 'auth_type', 'auth_config',
        'default_headers', 'timeout_ms', 'retry_policy', 'is_active', 'is_main',
      ],
    ),

    fields: {
      slug: {
        type: 'text',
        label: 'Slug',
        placeholder: 'e.g. sap-s4hana',
        helperText: 'Lowercase letters, numbers, and hyphens only',
        section: 'connection',
        order: 1,
        validation: {
          required: true,
          max: { value: 100, message: 'Slug must be 100 characters or fewer' },
          pattern: {
            value: '^[a-z0-9-]+$',
            message: 'Only lowercase letters, numbers, and hyphens are allowed',
          },
        },
      },
      name: {
        type: 'text',
        label: 'Name',
        placeholder: 'e.g. SAP S/4HANA',
        section: 'connection',
        order: 2,
        validation: {
          required: true,
          max: { value: 255, message: 'Name must be 255 characters or fewer' },
        },
      },
      base_url: {
        type: 'url',
        label: 'Base URL',
        placeholder: 'https://erp.example.com',
        section: 'connection',
        order: 3,
        validation: {
          required: true,
        },
      },
      environment: {
        type: 'select',
        label: 'Environment',
        helperText:
          'Which environment this registry points to (Production, Préprod, Recette, …)',
        section: 'connection',
        order: 4,
        options: {
          // The empty item is the way back to "unset" once a value is picked;
          // it submits '' which the API stores as null.
          items: [ERP_ENVIRONMENT_NONE_OPTION, ...ERP_ENVIRONMENT_OPTIONS],
          placeholder: ERP_ENVIRONMENT_PLACEHOLDER,
        },
      },
      auth_type: {
        type: 'select',
        label: 'Auth Type',
        section: 'auth',
        order: 5,
        validation: {
          required: true,
        },
        options: {
          items: [
            { value: 'api_key', label: 'API Key' },
            { value: 'oauth2_client', label: 'OAuth2 Client' },
            { value: 'basic', label: 'Basic' },
            { value: 'bearer', label: 'Bearer' },
            { value: 'login_bearer', label: 'Login Bearer' },
            { value: 'none', label: 'None' },
          ],
        },
      },
      auth_config: {
        type: 'textarea',
        label: 'Auth Configuration (JSON)',
        helperText: 'JSON auth configuration specific to auth type',
        placeholder: '{"client_id": "xxx", "client_secret": "yyy"}',
        section: 'auth',
        order: 6,
        validation: {
          required: true,
        },
        options: {
          rows: 5,
          resize: 'vertical',
        },
      },
      default_headers: {
        type: 'textarea',
        label: 'Default Headers (JSON)',
        placeholder: '{"X-Custom-Header": "value"}',
        section: 'advanced',
        order: 7,
        options: {
          rows: 3,
          resize: 'vertical',
        },
      },
      timeout_ms: {
        type: 'number',
        label: 'Timeout (ms)',
        placeholder: '30000',
        helperText: '1000-300000',
        section: 'advanced',
        order: 8,
        options: {
          min: 1000,
          max: 300000,
        },
      },
      retry_policy: {
        type: 'textarea',
        label: 'Retry Policy (JSON)',
        placeholder: '{"max_retries": 3, "backoff_ms": 1000}',
        section: 'advanced',
        order: 9,
        options: {
          rows: 3,
          resize: 'vertical',
        },
      },
      is_active: {
        type: 'checkbox',
        label: 'Active',
        section: 'advanced',
        order: 10,
        defaultValue: true,
      },
      is_main: {
        type: 'checkbox',
        label: 'Main Registry',
        helperText: 'Mark this registry as the main/primary one',
        section: 'advanced',
        order: 11,
        defaultValue: false,
      },
    },

    sections: [
      {
        id: 'connection',
        title: 'Connection Settings',
        order: 1,
      },
      {
        id: 'auth',
        title: 'Authentication',
        order: 2,
      },
      {
        id: 'advanced',
        title: 'Advanced Settings',
        order: 3,
      },
    ],

    actions: createFormActions('Registry', isEdit),

    workflow: createFormWorkflow('/admin/erp/registries'),

    layout: createFormLayout(),
  };
}
